import { Dialog } from '@base-ui/react/dialog';
import type { CollectionFilterConditions } from '@kiipu/app-protocol';
import {
  FilterChip,
  MenuCheckboxItem,
  MenuContent,
  MenuGroup,
  MenuItem,
  MenuLabel,
  MenuRoot,
  MenuSeparator,
  MenuSubmenuRoot,
  MenuSubmenuTrigger,
  MenuTrigger,
  Tooltip,
} from '@kiipu/ui';
import {
  ArrowClockwise,
  ArrowLeft,
  Article,
  CaretRight,
  ChatCircleText,
  Check,
  CheckCircle,
  CircleHalf,
  Clock,
  FunnelSimple,
  GlobeHemisphereWest,
  LinkSimple,
  Note,
  PlayCircle,
  TelevisionSimple,
  WarningCircle,
  WechatLogo,
  X,
  XLogo,
  YoutubeLogo,
} from '@phosphor-icons/react';
import {
  forwardRef,
  Fragment,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  useSyncExternalStore,
  type ComponentProps,
  type ReactNode,
} from 'react';
import './collection-filters.css';

const dimensions = ['platform', 'kind', 'status', 'quality'] as const;
type Dimension = (typeof dimensions)[number];
type Option = { value: string; label: string; icon: ReactNode };
const definitions: Record<
  Dimension,
  { label: string; noun: string; unit: string; icon: ReactNode; options: Option[] }
> = {
  platform: {
    label: '来源',
    noun: '来源',
    unit: '个',
    icon: <GlobeHemisphereWest size={15} />,
    options: [
      { value: 'web', label: '网页', icon: <GlobeHemisphereWest size={15} /> },
      { value: 'wechat', label: '公众号', icon: <WechatLogo size={15} /> },
      { value: 'zhihu', label: '知乎', icon: <ChatCircleText size={15} /> },
      { value: 'xiaohongshu', label: '小红书', icon: <Note size={15} /> },
      { value: 'weibo', label: '微博', icon: <ChatCircleText size={15} /> },
      { value: 'x', label: 'X', icon: <XLogo size={15} /> },
      { value: 'bilibili', label: '哔哩哔哩', icon: <TelevisionSimple size={15} /> },
      { value: 'youtube', label: 'YouTube', icon: <YoutubeLogo size={15} /> },
    ],
  },
  kind: {
    label: '内容类型',
    noun: '类型',
    unit: '个',
    icon: <Article size={15} />,
    options: [
      { value: 'article', label: '文章', icon: <Article size={15} /> },
      { value: 'post', label: '笔记', icon: <Note size={15} /> },
      { value: 'video', label: '视频', icon: <PlayCircle size={15} /> },
    ],
  },
  status: {
    label: '获取状态',
    noun: '状态',
    unit: '种',
    icon: <ArrowClockwise size={15} />,
    options: [
      { value: 'queued', label: '等待获取', icon: <Clock size={15} /> },
      { value: 'running', label: '正在获取', icon: <ArrowClockwise size={15} /> },
      { value: 'completed', label: '已完成', icon: <CheckCircle size={15} /> },
      { value: 'failed', label: '获取未完成', icon: <WarningCircle size={15} /> },
    ],
  },
  quality: {
    label: '保存完整度',
    noun: '完整度',
    unit: '种',
    icon: <CheckCircle size={15} />,
    options: [
      { value: 'complete', label: '完整保存', icon: <CheckCircle size={15} /> },
      { value: 'partial', label: '部分保存', icon: <CircleHalf size={15} /> },
      { value: 'link_only', label: '仅保存链接', icon: <LinkSimple size={15} /> },
    ],
  },
};

const mobileQuery = '(max-width: 767px)';
const isMobile = () => window.matchMedia(mobileQuery).matches;
function subscribeMobile(listener: () => void) {
  const query = window.matchMedia(mobileQuery);
  query.addEventListener('change', listener);
  return () => query.removeEventListener('change', listener);
}

function values(conditions: CollectionFilterConditions, dimension: Dimension): readonly string[] {
  return conditions[dimension] ?? [];
}

const TriggerButton = forwardRef<HTMLButtonElement, ComponentProps<'button'> & { count: number }>(
  function TriggerButton({ count, ...props }, ref) {
    const label = count ? `筛选，已启用 ${count} 项` : '添加筛选';
    return (
      <Tooltip content={label} side="left">
        <button
          {...props}
          ref={ref}
          type="button"
          aria-label={label}
          data-collection-filter-trigger
          className={`kiipu-filter-trigger${count ? ' is-active' : ''}`}
        >
          <FunnelSimple size={15} weight={count ? 'fill' : 'regular'} aria-hidden="true" />
          {count ? (
            <span className="kiipu-filter-count" aria-hidden="true">
              {count}
            </span>
          ) : null}
        </button>
      </Tooltip>
    );
  },
);

function OptionList({
  dimension,
  conditions,
  onToggle,
  onClear,
  mobile = false,
}: {
  dimension: Dimension;
  conditions: CollectionFilterConditions;
  onToggle: (value: string) => void;
  onClear: () => void;
  mobile?: boolean;
}) {
  const selected = values(conditions, dimension);
  // Freeze groups for this opening so a checkbox never moves beneath the pointer.
  const [groups] = useState(() => {
    const options = definitions[dimension].options;
    return [
      options.filter((option) => selected.includes(option.value)),
      options.filter((option) => !selected.includes(option.value)),
    ];
  });
  return (
    <>
      <div role={mobile ? 'group' : undefined} aria-label={definitions[dimension].label}>
        {groups.map((options, index) => (
          <Fragment key={index}>
            {index === 1 && groups[0].length && options.length ? (
              <MenuSeparator className="kiipu-filter-divider" />
            ) : null}
            {options.map((option) => {
              const checked = selected.includes(option.value);
              const content = (
                <>
                  <span
                    className={`kiipu-filter-checkbox${checked ? ' is-checked' : ''}`}
                    aria-hidden="true"
                  >
                    {checked ? <Check size={11} weight="bold" /> : null}
                  </span>
                  <span className="kiipu-filter-option-icon" aria-hidden="true">
                    {option.icon}
                  </span>
                  <span className="kiipu-filter-option-name">{option.label}</span>
                </>
              );
              return mobile ? (
                <button
                  key={option.value}
                  type="button"
                  role="checkbox"
                  aria-checked={checked}
                  className="kiipu-filter-option"
                  onClick={() => onToggle(option.value)}
                >
                  {content}
                </button>
              ) : (
                <MenuCheckboxItem
                  key={option.value}
                  unstyled
                  checked={checked}
                  closeOnClick={false}
                  className="kiipu-filter-option"
                  onCheckedChange={() => onToggle(option.value)}
                >
                  {content}
                </MenuCheckboxItem>
              );
            })}
          </Fragment>
        ))}
      </div>
      <MenuSeparator className="kiipu-filter-divider" />
      {mobile ? (
        <button
          type="button"
          className="kiipu-filter-option kiipu-filter-clear-condition"
          aria-disabled={!selected.length}
          onClick={() => {
            if (selected.length) onClear();
          }}
        >
          清除此条件
        </button>
      ) : (
        <MenuItem
          unstyled
          closeOnClick={false}
          disabled={!selected.length}
          className="kiipu-filter-option kiipu-filter-clear-condition"
          onClick={onClear}
        >
          清除此条件
        </MenuItem>
      )}
    </>
  );
}

export type CollectionFiltersProps = {
  conditions: CollectionFilterConditions;
  onChange: (next: CollectionFilterConditions) => void;
  onOpenChange?: (open: boolean) => void;
  children: (parts: {
    trigger: ReactNode;
    chips: ReactNode;
    hasActiveFilters: boolean;
  }) => ReactNode;
};

export function CollectionFilters({
  conditions,
  onChange,
  onOpenChange,
  children,
}: CollectionFiltersProps) {
  const mobile = useSyncExternalStore(subscribeMobile, isMobile, () => false);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const chipNodes = useRef(new Map<Dimension, HTMLButtonElement>());
  const returnFocus = useRef<HTMLButtonElement | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const latest = useRef(conditions);
  const notifyOpen = useRef(onOpenChange);
  const [desktopOpen, setDesktopOpen] = useState(false);
  const [desktopDimension, setDesktopDimension] = useState<Dimension | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileDimension, setMobileDimension] = useState<Dimension | null>(null);
  const [chipMenu, setChipMenu] = useState<{
    dimension: Dimension;
    node: HTMLButtonElement;
    rect: DOMRect;
  } | null>(null);
  const triggerId = `${useId()}-collection-filter`;
  const activeDimensions = dimensions.filter(
    (dimension) => values(conditions, dimension).length > 0,
  );
  const count = activeDimensions.length;
  const open = desktopOpen || mobileOpen || Boolean(chipMenu);

  useLayoutEffect(() => {
    latest.current = conditions;
  }, [conditions]);
  useLayoutEffect(() => {
    notifyOpen.current = onOpenChange;
  }, [onOpenChange]);
  useLayoutEffect(() => {
    notifyOpen.current?.(open);
  }, [open]);
  useLayoutEffect(() => () => notifyOpen.current?.(false), []);
  useLayoutEffect(() => {
    setDesktopOpen(false);
    setDesktopDimension(null);
    setMobileOpen(false);
    setMobileDimension(null);
    setChipMenu(null);
  }, [mobile]);
  useLayoutEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [mobileDimension]);

  function replaceDimension(dimension: Dimension, selected: readonly string[]) {
    const next = { ...latest.current };
    // Canonical enum ordering makes URLs, request keys and saved values agree.
    const ordered = definitions[dimension].options
      .map((option) => option.value)
      .filter((value) => selected.includes(value));
    if (ordered.length) Object.assign(next, { [dimension]: ordered });
    else delete next[dimension];
    latest.current = next;
    onChange(next);
  }
  function toggle(dimension: Dimension, value: string) {
    const selected = values(latest.current, dimension);
    replaceDimension(
      dimension,
      selected.includes(value) ? selected.filter((item) => item !== value) : [...selected, value],
    );
  }
  function restoreFocus() {
    return returnFocus.current?.isConnected ? returnFocus.current : triggerRef.current;
  }
  function edit(dimension: Dimension) {
    const node = chipNodes.current.get(dimension);
    if (!node) return;
    returnFocus.current = node;
    setDesktopOpen(false);
    setDesktopDimension(null);
    if (mobile) {
      setMobileDimension(dimension);
      setMobileOpen(true);
    } else setChipMenu({ dimension, node, rect: node.getBoundingClientRect() });
  }
  function remove(dimension: Dimension) {
    const remaining = activeDimensions.filter((item) => item !== dimension);
    replaceDimension(dimension, []);
    requestAnimationFrame(() =>
      (chipNodes.current.get(remaining[0]!) ?? triggerRef.current)?.focus({ preventScroll: true }),
    );
  }
  function clearAll() {
    latest.current = {};
    onChange({});
    requestAnimationFrame(() => triggerRef.current?.focus({ preventScroll: true }));
  }
  const optionList = (dimension: Dimension, touch = false) => (
    <OptionList
      key={dimension}
      dimension={dimension}
      conditions={conditions}
      onToggle={(value) => toggle(dimension, value)}
      onClear={() => replaceDimension(dimension, [])}
      mobile={touch}
    />
  );

  const trigger = mobile ? (
    <Dialog.Root
      open={mobileOpen}
      onOpenChange={(next) => {
        setMobileOpen(next);
        if (!next) setMobileDimension(null);
      }}
    >
      <Dialog.Trigger
        id={triggerId}
        onClick={() => {
          returnFocus.current = null;
        }}
        render={<TriggerButton count={count} ref={triggerRef} />}
      />
      <Dialog.Portal>
        <Dialog.Backdrop className="kiipu-filter-sheet-backdrop" />
        <Dialog.Popup className="kiipu-filter-sheet" finalFocus={restoreFocus}>
          <div className="kiipu-filter-sheet-heading">
            {mobileDimension ? (
              <button
                type="button"
                className="kiipu-filter-sheet-icon"
                aria-label="返回筛选条件"
                onClick={() => setMobileDimension(null)}
              >
                <ArrowLeft size={17} aria-hidden="true" />
              </button>
            ) : (
              <span className="kiipu-filter-sheet-icon" />
            )}
            <Dialog.Title>
              {mobileDimension ? definitions[mobileDimension].label : '添加筛选'}
            </Dialog.Title>
            <Dialog.Close className="kiipu-filter-sheet-icon" aria-label="关闭筛选">
              <X size={17} aria-hidden="true" />
            </Dialog.Close>
          </div>
          <Dialog.Description className="sr-only">
            选择筛选条件，修改后立即生效。同一条件可选择多项。
          </Dialog.Description>
          <div className="kiipu-filter-sheet-scroll" ref={scrollRef}>
            {mobileDimension
              ? optionList(mobileDimension, true)
              : dimensions.map((dimension) => (
                  <button
                    key={dimension}
                    type="button"
                    className="kiipu-filter-dimension"
                    onClick={() => setMobileDimension(dimension)}
                  >
                    <span className="kiipu-filter-option-icon" aria-hidden="true">
                      {definitions[dimension].icon}
                    </span>
                    <span className="kiipu-filter-option-name">{definitions[dimension].label}</span>
                    {values(conditions, dimension).length ? (
                      <span className="kiipu-filter-dimension-count">
                        {values(conditions, dimension).length} 项
                      </span>
                    ) : null}
                    <CaretRight size={13} aria-hidden="true" />
                  </button>
                ))}
          </div>
          {mobileDimension ? (
            <div className="kiipu-filter-sheet-footer">
              <span>
                {values(conditions, mobileDimension).length
                  ? `已选 ${values(conditions, mobileDimension).length} 项`
                  : '不限'}
              </span>
              <Dialog.Close className="kiipu-filter-done">完成</Dialog.Close>
            </div>
          ) : null}
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  ) : (
    <MenuRoot
      open={desktopOpen}
      closeParentOnEsc
      onOpenChange={(next) => {
        setDesktopOpen(next);
        if (!next) setDesktopDimension(null);
      }}
    >
      <MenuTrigger id={triggerId} render={<TriggerButton count={count} ref={triggerRef} />} />
      <MenuContent
        unstyled
        align="end"
        className="kiipu-filter-menu kiipu-filter-dimensions-menu"
        positionerStyle={{ zIndex: 110 }}
        onKeyDownCapture={(event) => {
          if (event.key === 'Escape') {
            event.preventDefault();
            event.stopPropagation();
            setDesktopOpen(false);
            setDesktopDimension(null);
          }
        }}
      >
        <MenuGroup>
          <MenuLabel unstyled className="kiipu-filter-menu-label">
            添加筛选
          </MenuLabel>
          {dimensions.map((dimension) => (
            <MenuSubmenuRoot
              key={dimension}
              open={desktopDimension === dimension}
              onOpenChange={(next) =>
                setDesktopDimension((current) =>
                  next ? dimension : current === dimension ? null : current,
                )
              }
            >
              <MenuSubmenuTrigger unstyled openOnHover className="kiipu-filter-dimension">
                <span className="kiipu-filter-option-icon" aria-hidden="true">
                  {definitions[dimension].icon}
                </span>
                <span className="kiipu-filter-option-name">{definitions[dimension].label}</span>
                {values(conditions, dimension).length ? (
                  <span className="kiipu-filter-dimension-count">
                    {values(conditions, dimension).length}
                  </span>
                ) : null}
                <CaretRight size={12} aria-hidden="true" />
              </MenuSubmenuTrigger>
              <MenuContent
                unstyled
                side="right"
                align="start"
                sideOffset={4}
                className="kiipu-filter-menu"
                positionerStyle={{ zIndex: 111 }}
              >
                <MenuGroup>
                  <MenuLabel unstyled className="kiipu-filter-menu-label">
                    {definitions[dimension].label}
                    <span>可多选</span>
                  </MenuLabel>
                  {optionList(dimension)}
                </MenuGroup>
              </MenuContent>
            </MenuSubmenuRoot>
          ))}
        </MenuGroup>
      </MenuContent>
    </MenuRoot>
  );

  const chips = count ? (
    <div className="kiipu-filter-chips" aria-label="当前筛选条件">
      {activeDimensions.map((dimension) => {
        const definition = definitions[dimension];
        const selected = values(conditions, dimension);
        const names = definition.options
          .filter((option) => selected.includes(option.value))
          .map((option) => option.label)
          .join('、');
        return (
          <FilterChip
            key={dimension}
            unstyled
            label={definition.label}
            value={
              selected.length > 1
                ? `${selected.length} ${definition.unit}${definition.noun}`
                : names
            }
            icon={<span aria-hidden="true">{definition.icon}</span>}
            operator={selected.length > 1 ? '属于任一' : '是'}
            editable
            showOperator
            popup={mobile ? 'dialog' : 'menu'}
            expanded={
              mobile
                ? mobileOpen && mobileDimension === dimension
                : chipMenu?.dimension === dimension
            }
            editLabel={`编辑${definition.label}筛选，当前为${names}`}
            removeLabel={`移除${definition.label}筛选`}
            onEdit={() => edit(dimension)}
            onRemove={() => remove(dimension)}
            renderEdit={(element) => <Tooltip content={names}>{element}</Tooltip>}
            slotProps={{
              root: { className: 'kiipu-filter-chip', 'data-filter-chip': dimension },
              label: { className: 'kiipu-filter-chip-label' },
              operator: { className: 'kiipu-filter-chip-operator' },
              value: { className: 'kiipu-filter-chip-value' },
              valueText: { className: 'kiipu-filter-chip-value-text' },
              edit: {
                className: 'kiipu-filter-chip-edit',
                ref: (node) => {
                  if (node) chipNodes.current.set(dimension, node);
                  else chipNodes.current.delete(dimension);
                },
              },
              remove: { className: 'kiipu-filter-chip-remove' },
            }}
          />
        );
      })}
      {count > 1 ? (
        <button type="button" className="kiipu-filter-clear-all" onClick={clearAll}>
          清除全部
        </button>
      ) : null}
    </div>
  ) : null;

  return (
    <>
      {children({ trigger, chips, hasActiveFilters: count > 0 })}
      <MenuRoot
        open={Boolean(chipMenu)}
        onOpenChange={(next) => {
          if (!next) setChipMenu(null);
        }}
      >
        {chipMenu ? (
          <MenuContent
            unstyled
            align="start"
            className="kiipu-filter-menu"
            positionerStyle={{ zIndex: 110 }}
            anchor={{
              getBoundingClientRect: () =>
                chipMenu.node.isConnected ? chipMenu.node.getBoundingClientRect() : chipMenu.rect,
            }}
            finalFocus={() => (chipMenu.node.isConnected ? chipMenu.node : triggerRef.current)}
          >
            <MenuGroup>
              <MenuLabel unstyled className="kiipu-filter-menu-label">
                {definitions[chipMenu.dimension].label}
                <span>可多选</span>
              </MenuLabel>
              {optionList(chipMenu.dimension)}
            </MenuGroup>
          </MenuContent>
        ) : null}
      </MenuRoot>
    </>
  );
}
